/**
 * Supabase Storage Service
 * Handles all API calls to Supabase backend
 */

import type { Prompt, Artwork } from '@/types';
import { getCachedPrompts, cachePrompts, invalidatePromptsCache } from './database';

// API base:
// - Web in production: relative path (frontend is served by the same backend).
// - Web in development: localhost Express.
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ||
  (import.meta.env.DEV ? 'http://localhost:3001/api' : '/api');
const API_TIMEOUT = 30000; // 30 seconds (increased for large image payloads)

/**
 * Make API call with timeout
 */
async function apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    const adminKey = typeof localStorage !== 'undefined' ? localStorage.getItem('pb_admin_key') : null;
    const response = await fetch(`${BACKEND_URL}/${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(adminKey ? { 'x-admin-key': adminKey } : {}),
        ...options.headers,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `API error: ${response.status}`);
    }

    // Handle 204 No Content responses (DELETE operations)
    if (response.status === 204) {
      return null as T;
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out');
    }
    throw error;
  }
}

/**
 * Public aggregate stats (counts only — no PII). Used by the About panel.
 */
export async function fetchStats(): Promise<{ prompts: number; people: number; drawings: number; artists: number }> {
  return apiCall('stats', { method: 'GET' });
}

/**
 * Prompt Storage API
 */
export const promptStorage = {
  async getAll(options?: { useCache?: boolean; backgroundRefresh?: boolean }): Promise<Prompt[]> {
    const useCache = options?.useCache !== false; // Default to true
    const backgroundRefresh = options?.backgroundRefresh !== false; // Default to true

    // Try to get cached data first
    if (useCache) {
      const cached = await getCachedPrompts();
      if (cached) {
        console.log(`[promptStorage.getAll] Returning ${cached.length} cached prompts`);

        // Optionally refresh cache in background
        if (backgroundRefresh) {
          this.refreshPromptsCache().catch(err => {
            console.error('[promptStorage.getAll] Background refresh failed:', err);
          });
        }

        return cached;
      }
    }

    // Cache miss or disabled - fetch from API
    console.log('[promptStorage.getAll] Cache miss, fetching from API');
    return this.fetchAndCachePrompts();
  },

  async fetchAndCachePrompts(): Promise<Prompt[]> {
    // Express returns array directly, not wrapped
    // Load ALL prompts (the API default is 50; 1000 used to truncate once we passed
    // a thousand). High ceiling so the count and the chart analysis cover everything.
    const promptsData = await apiCall<any[]>('prompts?limit=100000', {
      method: 'GET',
    });

    console.log('[promptStorage.fetchAndCachePrompts] Sample prompt from API:', promptsData[0]);

    // Transform API response to Prompt format
    // Backend already orders by createdAt DESC and assigns sequential promptNumbers
    const prompts = promptsData.map((p: any) => ({
      id: p.id,
      prompt: p.prompt,
      email: p.email || '',
      status: p.status || 'pending',
      createdAt: p.timestamp || p.createdAt || p.created_at || Date.now(),
      ...(p.completedAt && { completedAt: p.completedAt }),
      ...(p.completed_at && { completedAt: p.completed_at }),
      ...(p.artworkId && { artworkId: p.artworkId }),
      ...(p.artwork_id && { artworkId: p.artwork_id }),
      // Backend provides promptNumber (or prompt_number)
      promptNumber: p.promptNumber || p.prompt_number,
    }));

    console.log('[promptStorage.fetchAndCachePrompts] Transformed prompts sample:', prompts[0]);

    // Sort by prompt number (newest first = highest number first)
    // Ensure we treat promptNumber as a number for proper sorting
    const sorted = prompts.sort((a, b) => {
      const numA = Number(a.promptNumber) || 0;
      const numB = Number(b.promptNumber) || 0;
      return numB - numA;
    });

    console.log('[promptStorage.fetchAndCachePrompts] Sorted prompts - first:', sorted[0]?.promptNumber, 'last:', sorted[sorted.length - 1]?.promptNumber);

    // Cache the prompts
    await cachePrompts(sorted);

    return sorted;
  },

  async refreshPromptsCache(): Promise<void> {
    console.log('[promptStorage.refreshPromptsCache] Starting background refresh');
    await this.fetchAndCachePrompts();
  },

  async create(prompt: string, email: string): Promise<Prompt> {
    console.log('[promptStorage.create] Creating prompt with email:', email);

    const id = `prompt_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const response = await apiCall<{ id: string; prompt: string; email: string; status: string; createdAt: string }>('prompts', {
      method: 'POST',
      body: JSON.stringify({ id, prompt, email }),
    });

    console.log('[promptStorage.create] Prompt created');

    // Invalidate cache to ensure fresh data on next fetch
    await invalidatePromptsCache();

    // Note: Email is sent by the backend when the prompt is created,
    // so we don't need to send it here to avoid duplicates

    return {
      id: response.id,
      prompt,
      email,
      status: 'pending',
      createdAt: Date.now(),
    };
  },

  async delete(id: string): Promise<void> {
    await apiCall(`prompts/${id}`, {
      method: 'DELETE',
    });

    // Invalidate cache after deletion
    await invalidatePromptsCache();
  },

  async addEmail(id: string, email: string): Promise<void> {
    await apiCall(`prompts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ email }),
    });

    // Invalidate cache after update
    await invalidatePromptsCache();
  },

  async complete(id: string): Promise<void> {
    await apiCall(`prompts/${id}/complete`, {
      method: 'PATCH',
    });

    // Invalidate cache after completion
    await invalidatePromptsCache();
  },
};

/**
 * Artwork Storage API
 */
export const artworkStorage = {
  async getAll(): Promise<Artwork[]> {
    // Express returns array directly for approved artworks
    const artworksData = await apiCall<any[]>('artworks?status=approved&limit=10000', {
      method: 'GET',
    });

    console.log('[artworkStorage.getAll] RAW API Response - Full first artwork:', JSON.stringify(artworksData[0], null, 2));
    console.log('[artworkStorage.getAll] RAW API Response - All field names:', Object.keys(artworksData[0] || {}));

    // Transform API response to Artwork format
    const artworks = artworksData.map((a: any) => {
      const transformed = {
        id: a.id,
        promptNumber: a.promptNumber ?? a.prompt_number ?? a.promptnumber ?? null,
        promptText: a.prompt || a.promptText || a.prompt_text || a.prompttext || null,
        imageData: a.imageData ?? a.image_data ?? a.imageUrl ?? a.imageurl ?? a.image_url ?? a.image,
        promptId: a.promptId ?? a.prompt_id ?? a.promptid ?? null,
        artistName: a.artistName ?? a.artist_name ?? a.artistname ?? null,
        artistEmail: a.artistEmail ?? a.artist_email ?? a.artistemail ?? null,
        hasEmail: a.hasEmail ?? !!(a.artistEmail ?? a.artist_email),
        source: a.source ?? null,
        status: 'approved' as const,
        createdAt: a.createdAt ?? a.created_at ?? a.timestamp ?? Date.now(),
        approvedAt: a.approvedAt ?? a.approved_at ?? a.timestamp ?? null,
        isAdminCreated: a.isAdminCreated ?? a.is_admin_created ?? (a.type !== 'user_artwork'),
        heartCount: a.heartCount ?? a.heart_count ?? 0,
      };

      console.log('[artworkStorage.getAll] Field extraction check:', {
        'promptNumber found': a.promptNumber ?? a.prompt_number ?? a.promptnumber ?? 'MISSING',
        'promptId found': a.promptId ?? a.prompt_id ?? a.promptid ?? 'MISSING',
        'promptText found': a.prompt ?? a.promptText ?? a.prompt_text ?? a.prompttext ?? 'MISSING',
      });

      return transformed;
    });

    console.log('[artworkStorage.getAll] Transformed artwork sample:', artworks[0]);
    return artworks;
  },

  async getApproved(): Promise<Artwork[]> {
    return this.getAll();
  },

  async getPending(): Promise<Artwork[]> {
    try {
      // Express returns array directly for pending artworks
      const artworksData = await apiCall<any[]>('artworks?status=pending&limit=10000', {
        method: 'GET',
      });

      console.log('[artworkStorage.getPending] RAW API Response - Full first artwork:', JSON.stringify(artworksData[0], null, 2));
      console.log('[artworkStorage.getPending] RAW API Response - All field names:', Object.keys(artworksData[0] || {}));

      const pendingArtworks = artworksData.map((a: any) => {
        const transformed = {
          id: a.id,
          promptNumber: a.promptNumber ?? a.prompt_number ?? a.promptnumber ?? null,
          imageData: a.imageData ?? a.image_data ?? a.imageUrl ?? a.imageurl ?? a.image_url ?? a.image,
          promptId: a.promptId ?? a.prompt_id ?? a.promptid ?? null,
          promptText: a.prompt || a.promptText || a.prompt_text || a.prompttext,
          artistName: a.artistName ?? a.artist_name ?? a.artistname ?? null,
          artistEmail: a.artistEmail ?? a.artist_email ?? a.artistemail ?? null,
          hasEmail: a.hasEmail ?? !!(a.artistEmail ?? a.artist_email),
          source: a.source ?? null,
          status: 'pending' as const,
          createdAt: a.createdAt ?? a.created_at ?? a.timestamp ?? Date.now(),
          isAdminCreated: false,
        };

        console.log('[artworkStorage.getPending] Field extraction check:', {
          'promptNumber found': a.promptNumber ?? a.prompt_number ?? a.promptnumber ?? 'MISSING',
          'promptId found': a.promptId ?? a.prompt_id ?? a.promptid ?? 'MISSING',
          'promptText found': a.prompt ?? a.promptText ?? a.prompt_text ?? a.prompttext ?? 'MISSING',
        });

        return transformed;
      });

      console.log('[artworkStorage.getPending] Transformed pending artwork:', pendingArtworks[0]);
      return pendingArtworks;
    } catch (error) {
      console.error('[artworkStorage.getPending] Error fetching pending artworks:', error);
      // Return empty array on timeout/error to prevent app crash
      return [];
    }
  },

  async getById(id: string): Promise<Artwork | undefined> {
    try {
      const allArtworks = await this.getAll();
      const pendingArtworks = await this.getPending();
      return [...allArtworks, ...pendingArtworks].find(a => a.id === id);
    } catch (error) {
      console.error('Error fetching artwork:', error);
      return undefined;
    }
  },

  async create(data: {
    imageData: string;
    promptId?: string;
    promptText?: string;
    promptNumber?: number;
    artistName?: string;
    artistEmail?: string;
    isAdminCreated: boolean;
  }): Promise<Artwork> {
    const id = `artwork_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    if (data.isAdminCreated) {
      // Admin artwork
      const response = await apiCall<{ id: string }>('artworks', {
        method: 'POST',
        body: JSON.stringify({
          id,
          imageData: data.imageData,
          promptNumber: data.promptNumber || 1,
          isAdminCreated: true,
        }),
      });

      return {
        id: response.id,
        promptNumber: data.promptNumber ?? null,
        imageData: data.imageData,
        status: 'approved',
        createdAt: Date.now(),
        approvedAt: Date.now(),
        isAdminCreated: true,
      };
    } else {
      // User artwork submission
      const payload = {
        id,
        promptId: data.promptId,
        promptText: data.promptText || '',
        promptNumber: data.promptNumber || 1,
        imageData: data.imageData,
        artistName: data.artistName || '',
        artistEmail: data.artistEmail,
        isAdminCreated: false,
        source: 'web',
      };

      console.log('[artworkStorage.create] Submitting user artwork with payload:', {
        ...payload,
        imageData: payload.imageData ? `${payload.imageData.substring(0, 50)}... (${payload.imageData.length} chars)` : 'none',
      });

      const response = await apiCall<{ id: string }>('artworks', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      console.log('[artworkStorage.create] Backend response:', response);

      // Note: Artwork submission email is sent by the backend when the artwork is created,
      // so we don't need to send it here to avoid duplicates

      return {
        id: response.id,
        promptNumber: data.promptNumber || 0,
        imageData: data.imageData,
        promptId: data.promptId,
        promptText: data.promptText,
        artistName: data.artistName,
        artistEmail: data.artistEmail,
        status: 'pending',
        createdAt: Date.now(),
        isAdminCreated: false,
      };
    }
  },

  async approve(id: string): Promise<void> {
    console.log('[artworkStorage.approve] Approving artwork with ID:', id);
    const response = await apiCall(`artworks/${id}/approve`, {
      method: 'PATCH',
    });
    console.log('[artworkStorage.approve] Backend response:', response);
  },

  async reject(id: string): Promise<void> {
    // Archive: keep the drawing as a 'rejected' record (it lives in the artist's
    // profile archive) and reopen the prompt. Use delete() for a hard purge.
    await apiCall(`artworks/${id}/reject`, {
      method: 'POST',
    });
  },

  async delete(id: string): Promise<void> {
    await apiCall(`artworks/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Admin Storage API
 */
export const adminStorage = {
  // localStorage (not sessionStorage) so the login survives reloads, new tabs
  // and closing the tab, and stays in step with pb_admin_key.
  login(): void {
    try {
      localStorage.setItem('admin_authenticated', 'true');
      localStorage.setItem('admin_session_created', Date.now().toString());
    } catch { /* ignore */ }
  },

  logout(): void {
    try {
      localStorage.removeItem('admin_authenticated');
      localStorage.removeItem('admin_session_created');
      sessionStorage.removeItem('admin_authenticated');
      sessionStorage.removeItem('admin_session_created');
    } catch { /* ignore */ }
  },

  isLoggedIn(): boolean {
    try {
      return localStorage.getItem('admin_authenticated') === 'true'
        || sessionStorage.getItem('admin_authenticated') === 'true';
    } catch {
      return false;
    }
  },
};

/* ---------------- Admin activity (accounts + most-hearted drawings) ---------------- */

export interface AdminActivityAccount {
  alias: string;
  emailMasked: string;
  verified: boolean;
  createdAt: string;
  drawings: number;
}

export interface AdminActivityHearted {
  id: string;
  imageUrl?: string;
  promptNumber?: number;
  artistName?: string;
  promptText?: string;
  heartCount: number;
}

export interface AdminActivity {
  accounts: AdminActivityAccount[];
  accountCount: number;
  verifiedCount: number;
  hearted: AdminActivityHearted[];
  heartedCount: number;
  totalHearts: number;
}

/** Owner-only activity feed for the admin panel (admin key auto-attached). */
export async function getAdminActivity(): Promise<AdminActivity> {
  return apiCall<AdminActivity>('admin/activity', { method: 'POST', body: JSON.stringify({}) });
}


// ── Zine 2.0: most-liked drawings + one-tap permission email ──
export interface TopLikedArtwork {
  id: string;
  promptNumber: number | null;
  artistName: string | null;
  artistEmail: string | null;
  imageUrl: string | null;
  promptText: string | null;
  heartCount: number;
  zineSent: boolean;
  zineAccepted: boolean;
  zineDeclined: boolean;
  sentAt: string | null;
  acceptedAt: string | null;
  declinedAt: string | null;
  zineIssue: number | null;
}

export async function getTopLiked(limit = 2000): Promise<TopLikedArtwork[]> {
  const r = await apiCall<{ artworks: TopLikedArtwork[] }>(`admin/top-liked?limit=${limit}`, { method: 'GET' });
  return r.artworks || [];
}

export async function sendZineInvite(artworkId: string): Promise<{ ok: boolean; sentTo?: string }> {
  return apiCall(`admin/zine-invite`, { method: 'POST', body: JSON.stringify({ artworkId }) });
}

// ── Participant emails, for outreach (admin-only) ──
export type EmailCohort = 'accepted' | 'invited' | 'artists' | 'prompters';
export interface Participant {
  email: string;
  name: string | null;
  at: string | null;
  drawings: number;
}

export async function getParticipantEmails(cohort: EmailCohort): Promise<Participant[]> {
  const r = await apiCall<{ people: Participant[] }>(`admin/emails?cohort=${cohort}`, { method: 'GET' });
  return r.people || [];
}

export async function setZineIssue(artworkId: string, issue: number | null): Promise<{ ok: boolean; zineIssue: number | null }> {
  return apiCall(`admin/zine-issue`, { method: 'POST', body: JSON.stringify({ artworkId, issue }) });
}

/** Fetch the high-res (PNG) image blob for one drawing from the admin download proxy. */
async function fetchZineImageBlob(a: TopLikedArtwork): Promise<{ blob: Blob; ext: string }> {
  const adminKey = typeof localStorage !== 'undefined' ? localStorage.getItem('pb_admin_key') : null;
  const resp = await fetch(`${BACKEND_URL}/admin/zine-download?artworkId=${encodeURIComponent(a.id)}`, {
    headers: adminKey ? { 'x-admin-key': adminKey } : {},
  });
  if (!resp.ok) throw new Error(`Image ${a.promptNumber ?? a.id} failed (${resp.status})`);
  const blob = await resp.blob();
  const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
  return { blob, ext };
}

function triggerBlobDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function fileSlug(a: TopLikedArtwork): string {
  return (
    String(a.artistName || 'artist')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40) || 'artist'
  );
}

/** Download the high-res image for one accepted drawing (admin-only, forces a file save). */
export async function downloadZineImage(a: TopLikedArtwork): Promise<void> {
  const { blob, ext } = await fetchZineImageBlob(a);
  triggerBlobDownload(blob, `promptbrush-${a.promptNumber ?? 'drawing'}-${fileSlug(a)}.${ext}`);
}

function csvCell(v: unknown): string {
  return `"${String(v ?? '').replace(/"/g, '""')}"`;
}

/**
 * Download a whole set of accepted drawings as a single .zip: the hi-res images plus a
 * credits.csv (prompt #, artist, prompt, accepted date, marks, issue). Images are fetched
 * with small concurrency; failures are listed in MISSING.txt rather than aborting the zip.
 */
export async function downloadZineZip(
  items: TopLikedArtwork[],
  zipBaseName: string,
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  if (items.length === 0) throw new Error('Nothing to export.');
  const { zipSync, strToU8 } = await import('fflate');
  const folder = zipBaseName.replace(/[^a-z0-9-]+/gi, '-').replace(/^-|-$/g, '') || 'zine';
  const files: Record<string, Uint8Array> = {};
  const failed: string[] = [];
  let done = 0;

  // Bounded-concurrency fetch; index prefix keeps filenames unique + ordered.
  const CONC = 4;
  let idx = 0;
  const worker = async () => {
    while (idx < items.length) {
      const i = idx++;
      const a = items[i];
      const prefix = String(i + 1).padStart(3, '0');
      const num = a.promptNumber ? String(a.promptNumber).padStart(4, '0') : `id-${a.id.slice(-6)}`;
      try {
        const { blob, ext } = await fetchZineImageBlob(a);
        files[`${folder}/${prefix}_${num}-${fileSlug(a)}.${ext}`] = new Uint8Array(await blob.arrayBuffer());
      } catch {
        failed.push(`#${num} (${a.artistName || 'unknown'})`);
      }
      onProgress?.(++done, items.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONC, items.length) }, worker));

  const credits = [
    'prompt_number,artist,prompt,accepted_date,marks,zine_issue',
    ...items.map((a) =>
      [a.promptNumber ?? '', a.artistName ?? '', a.promptText ?? '', a.acceptedAt ?? '', a.heartCount, a.zineIssue ?? '']
        .map(csvCell)
        .join(','),
    ),
  ].join('\n');
  files[`${folder}/credits.csv`] = strToU8(credits);
  if (failed.length) files[`${folder}/MISSING.txt`] = strToU8(`Could not fetch images for:\n${failed.join('\n')}\n`);

  const zipped = zipSync(files, { level: 0 }); // store — PNGs are already compressed
  triggerBlobDownload(new Blob([zipped], { type: 'application/zip' }), `${folder}.zip`);
}

/** Public list of verified aliases — drives the ✓ badge on the gallery. */
export async function getVerifiedAliases(): Promise<string[]> {
  try {
    const list = await apiCall<string[]>('credits/verified', { method: 'GET' });
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/* ---------------- Hearts (web — anonymous local identity) ---------------- */

const HEART_ID_KEY = 'pb_heart_id';

/** A stable anonymous id for this browser, so web visitors can heart without an account. */
export function heartId(): string {
  if (typeof localStorage === 'undefined') return 'anon:web';
  let id = localStorage.getItem(HEART_ID_KEY);
  if (!id) {
    id = 'anon:' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    try { localStorage.setItem(HEART_ID_KEY, id); } catch { /* ignore */ }
  }
  return id;
}

/** Artwork ids this browser has hearted (server is the source of truth). */
export async function getHeartedIds(): Promise<string[]> {
  try {
    const j = await apiCall<{ ids?: string[] }>(`hearts?email=${encodeURIComponent(heartId())}`, { method: 'GET' });
    return Array.isArray(j?.ids) ? j.ids : [];
  } catch {
    return [];
  }
}

/** Toggle a heart on a drawing; returns the resulting hearted state. */
export async function toggleHeart(artworkId: string, notify = true): Promise<boolean> {
  const j = await apiCall<{ hearted: boolean }>('hearts/toggle', {
    method: 'POST',
    body: JSON.stringify({ email: heartId(), artworkId, notify }),
  });
  return !!j?.hearted;
}

/* ---------------- Artist profile (public) ---------------- */

export interface ArtistProfile {
  alias: string;
  verified: boolean;
  bio?: string | null;
  website?: string | null;
  avatar?: string | null;
  count: number;
  totalHearts: number;
  artworks: Array<{
    id: string;
    promptNumber?: number | null;
    promptText?: string | null;
    image?: string;
    imageData?: string;
    heartCount: number;
    createdAt?: string | number;
  }>;
}

/** Public profile: every approved drawing by a display name. */
export async function getArtistProfile(alias: string): Promise<ArtistProfile | null> {
  try {
    return await apiCall<ArtistProfile>(`artists/${encodeURIComponent(alias)}`, { method: 'GET' });
  } catch {
    return null;
  }
}
